import {
    GetLaborAPIResponse,
    GetMaterialAPIResponse,
    GetCustomerAPIResopnse,
    GetPOrdFileAPIResopnse,
    YardiOrderSummaryPayload,
} from '../utils/interfaces/place-order.interface'
import { trimTrailingSpacesInArray } from '../utils'
import { authQueryFn } from './auth'
import { axiosInstance } from './api'
import { AbortController, useSentryQuery } from '../utils/useSentryQuery'

const DEFAULT_YARDI_BASE_URL = [
    'https://customerportal.bms.my.broadlume.com',
].includes(window.location.origin)
    ? 'https://yardi.bms.my.broadlume.com'
    : 'https://dev.yardi.bms.my.broadlume.com'

const configuredYardiBaseUrl =
    import.meta.env.VITE_YARDI_SERVICE_BASE_URL?.trim() ||
    DEFAULT_YARDI_BASE_URL

const yardiBaseUrl = configuredYardiBaseUrl.replace(/\/+$/, '')

export async function getCustomer({
    signal,
}: Parameters<
    Parameters<typeof useSentryQuery>[0]['queryFn']
>[0]): Promise<GetCustomerAPIResopnse> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'get_customer',
        alias: userDetails.api_config,
        branch: userDetails?.Branches || userDetails.Branch,
        company: userDetails.Company,
        properties: userDetails.Properties,
    })
    return axiosInstance
        .get('/api/get_customer', {
            params: queryParam,
            signal,
        })
        .then((resp) => resp.data)
}

export async function getCustAddr({
    signal,
}: {
    signal: AbortController
}): Promise<GetCustomerAPIResopnse> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'get_custaddr',
        alias: userDetails.api_config,
        branch: userDetails.Branch,
        company: userDetails.Company,
        properties: userDetails.Properties,
    })
    return axiosInstance
        .get('/api/get_custaddr', {
            params: queryParam,
            signal,
        })
        .then((resp) => resp.data)
}

export async function getPordFile({
    signal,
}: Parameters<
    Parameters<typeof useSentryQuery>[0]['queryFn']
>[0]): Promise<GetPOrdFileAPIResopnse> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'get_pordfile',
        alias: userDetails.api_config,
        branch: userDetails.Branch,
        company: userDetails.Company,
        properties: userDetails.Properties,
    })

    return axiosInstance
        .get('/api/get_pordfile', {
            params: queryParam,
            signal,
        })
        .then((resp) => resp.data)
}

export async function getLabor({
    property,
    customer,
    shipTo,
    signal,
}: {
    property: string
    customer: string
    shipTo: string
    signal: AbortController
}): Promise<GetLaborAPIResponse> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'get_labor',
        alias: userDetails.api_config,
        property,
        customer,
        shipTo,
        branch: userDetails.Branch,
        company: userDetails.Company,
    })

    return axiosInstance
        .get('/api/get_labor', {
            params: queryParam,
            signal,
        })
        .then(
            (resp) =>
                trimTrailingSpacesInArray(resp.data) as GetLaborAPIResponse
        )
}

export async function getMaterial({
    property,
    customer,
    shipTo,
    signal,
}: {
    property: string
    customer: string
    shipTo: string
    signal: AbortController
}): Promise<GetMaterialAPIResponse> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        function: 'get_material',
        alias: userDetails.api_config,
        property,
        customer,
        shipTo,
        branch: userDetails.Branch,
        company: userDetails.Company,
    })

    return axiosInstance
        .get('/api/get_material_portal', {
            params: queryParam,
            signal,
        })
        .then(
            (resp) =>
                trimTrailingSpacesInArray(resp.data) as GetMaterialAPIResponse
        )
}

export async function createOrder({
    property,
    customer,
    shipTo,
    transaction,
    header,
    lines,
    notes,
    comments,
    ...rest
}: {
    property: string
    customer: string
    shipTo: string
    transaction: string
    header: string
    lines: string
    notes: string
    comments: string
}): Promise<Record<string, string>> {
    const { userDetails } = authQueryFn()
    const queryParam = new URLSearchParams({
        alias: userDetails.api_config,
        branch: userDetails.Branch,
        company: userDetails.Company,
        properties: userDetails.Properties,
        property,
        customer,
        shipTo,
        transaction,
        header,
        lines,
        notes,
        comments,
        ...rest,
    })
    return axiosInstance
        .get('/api/create_order', {
            params: queryParam,
        })
        .then((resp) => resp.data)
}

export const createOrderHeader = (payload: Array<string> = []) => {
    const { userDetails } = authQueryFn()
    const separator = `/*\\`
    const headerNodeList: string[] = [
        // userDetails.Customer,
        ...payload,
        userDetails.Branch,
    ]
    return headerNodeList.join(separator)
}

export const createYardiOrderSummary = async (
    payload: YardiOrderSummaryPayload
) => {
    return axiosInstance
        .post(`${yardiBaseUrl}/Yardi/SendOrderSummary`, payload)
        .then((resp) => resp.data)
}
